import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { AfterViewInit, CUSTOM_ELEMENTS_SCHEMA, Component, DestroyRef, OnInit, computed, effect, inject, untracked } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from '@app/services/auth.service';
import { PaymentService } from '@app/services/payment.service';
import { TabService } from '@app/services/tab.service';
import { VehicleDetailsSidebarComponent } from '@app/components/vehicle-details-sidebar/vehicle-details-sidebar.component';
import { ProductListComponent } from '@app/payment/product-list/product-list.component';
import { TrialExpiredDialogComponent } from '@app/payment/trial-expired-dialog/trial-expired-dialog.component';
import { ChatTabsComponent } from './chat-tabs/chat-tabs.component';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    ChatTabsComponent,
    VehicleDetailsSidebarComponent,
    TrialExpiredDialogComponent,
    ProductListComponent,
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ChatComponent implements OnInit, AfterViewInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  paymentService = inject(PaymentService);
  authService = inject(AuthService);
  tabService = inject(TabService);

  urlProp: string | undefined;

  /**
   * Reads the tabId from the URL and sets
   * the active tab accordingly.
   * If no tabId is found, it will redirect to the first tab.
   */
  ngOnInit(): void {
    const tabId = this.route.snapshot.paramMap.get('tabId');
    this.urlProp = tabId || undefined;

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const tabId = this.route.snapshot.paramMap.get('tabId');
        this.urlProp = tabId || undefined;
        this._setActiveTabOrRedirect(tabId || undefined);
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (!this.subscriptionExpired()) {
        this.tabService.showFeatureNotice();
      }
    }, 500);
  }

  subscriptionExpired = computed(() => this.paymentService.subscriptionExpired());

  onServerTabsChange = effect(() => this._setActiveTabOrRedirect(this.urlProp || undefined));

  private _setActiveTabOrRedirect(tabId: string | undefined): void {
    if (this.tabService.tabsLoading()) {
      return;
    }

    const untrackedActiveTab = untracked(() => this.tabService.activeTab());

    if (tabId && tabId === untrackedActiveTab?._id) {
      return;
    }
    if (tabId) {
      // find from existing tabs and set active
      const existingTab = this.tabService.getTabById(tabId);
      if (existingTab) {
        this.tabService.openTab(existingTab._id);
      } else {
        const firstTab = this.tabService.tabs()[0];
        if (firstTab) {
          this.router.navigate(['/chat', firstTab._id]);
        } else {
          this.tabService.openTab(undefined);
        }
      }
    } else {
      // no tabId in url, so navigate to first tab if exists
      const firstServerTab = this.tabService.tabs()[0];
      if (firstServerTab) {
        this.router.navigate(['/chat', firstServerTab?._id]);
      }
    }
    // else show empty-message
  }
}
